// OAuth 2.1 Server do Supabase Auth (beta) para o conector MCP do Claude:
// tela /oauth/consent e "Apps conectados" em Configurações › API. Isolado
// aqui porque a API `supabase.auth.oauth` ainda pode mudar.
// Regras: docs/specs/flows/dashboard-api-mcp.md R13.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface OAuthAuthorizationRequest {
  authorizationId: string;
  clientId: string;
  clientName: string;
  clientUri: string;
  scope: string;
  /** Presente quando o consentimento já foi dado: só redirecionar. */
  redirectUrl: string | null;
}

export interface OAuthConnectedApp {
  clientId: string;
  name: string;
  uri: string;
  grantedAt: string;
}

/** Detalhes do pedido de autorização (só com sessão: a página garante o login antes). */
export function useOAuthAuthorization(authorizationId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["oauth-authorization", authorizationId],
    enabled: !!authorizationId && enabled,
    retry: false,
    // O pedido expira e é de uso único: nada de refetch ao focar a aba.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    meta: { silentError: true },
    queryFn: async (): Promise<OAuthAuthorizationRequest> => {
      const { data, error } = await supabase.auth.oauth.getAuthorizationDetails(
        authorizationId as string,
      );
      if (error || !data) throw error ?? new Error("not_found");
      return {
        authorizationId: data.authorization_id,
        clientId: data.client.id,
        clientName: data.client.name,
        clientUri: data.client.uri,
        scope: data.scope,
        redirectUrl: data.redirect_url ?? null,
      };
    },
  });
}

/**
 * Aprova sem redirecionar: quem decide se o navegador vai para o
 * `redirect_url` é a página, depois de conferir que ele é do Claude.
 */
export function useApproveOAuthAuthorization() {
  return useMutation({
    mutationFn: async (authorizationId: string): Promise<string> => {
      const { data, error } = await supabase.auth.oauth.approveAuthorization(authorizationId, {
        skipBrowserRedirect: true,
      });
      if (error || !data?.redirect_url) throw error ?? new Error("approve_failed");
      return data.redirect_url;
    },
  });
}

export function useDenyOAuthAuthorization() {
  return useMutation({
    mutationFn: async (authorizationId: string): Promise<string | null> => {
      const { data, error } = await supabase.auth.oauth.denyAuthorization(authorizationId, {
        skipBrowserRedirect: true,
      });
      if (error) throw error;
      return data?.redirect_url ?? null;
    },
  });
}

/** Apps que o admin logado autorizou via OAuth (Configurações › API). */
export function useOAuthConnectedApps() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["oauth-grants", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<OAuthConnectedApp[]> => {
      const { data, error } = await supabase.auth.oauth.listGrants();
      if (error) throw error;
      return (data ?? []).map((g) => ({
        clientId: g.client.id,
        name: g.client.name,
        uri: g.client.uri,
        grantedAt: g.granted_at,
      }));
    },
  });
}

/** Revoga o acesso de um app (Supabase invalida os refresh tokens dele). */
export function useRevokeOAuthApp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (clientId: string) => {
      const { error } = await supabase.auth.oauth.revokeGrant({ clientId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["oauth-grants"] });
      toast.success("Acesso revogado");
    },
    onError: () => toast.error("Não foi possível revogar o acesso."),
  });
}

/**
 * Papel admin lido direto (não o `isAdmin` do AuthProvider, que fica `false`
 * por um instante logo após o login e faria a tela recusar um admin).
 */
export function useIsAdminUser(userId: string | undefined) {
  return useQuery({
    queryKey: ["is-admin", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId as string)
        .eq("role", "admin")
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
  });
}
